import { useEffect } from 'react';
import type { FieldValues, UseFormReturn, Path } from 'react-hook-form';
import { getErrorDetails } from '../errors/GraphQLAPIService';
import { getFriendlyErrorMessage } from '../errors/GraphQLAPIService';

export const FORM_API_ERROR_FIELD = 'apiError';

export type GraphQlErrorHandler = (exception: unknown) => void;

export interface FormApiError {
  type: 'manual';
  message: string;
  code: string;
  raw: { message?: string | string[]; path?: string | string[]; original: unknown };
}

export const useGraphQLApiError = <T extends FieldValues>(
  methods: Pick<UseFormReturn<T>, 'watch' | 'setError' | 'clearErrors' | 'formState'>
) => {
  const onError = (e: unknown): void => {
    const details = getErrorDetails(e);

    const fallbackMessage = getFriendlyErrorMessage({
      code: details?.code,
      message: details?.message,
    });

    methods.setError(FORM_API_ERROR_FIELD as Path<T>, {
      type: 'manual',
      message: fallbackMessage,
      // @ts-expect-error for admin debug
      code: details?.code,
      raw: { message: details?.message, path: details?.path, original: e },
    } satisfies FormApiError);
  };

  const onErrorAndThrow = (exception: unknown): never => {
    onError(exception);
    throw exception;
  };

  useEffect(() => {
    const subscription = methods.watch((_, { name }) => {
      if (!name) return;

      if (name !== FORM_API_ERROR_FIELD && methods.formState.errors[FORM_API_ERROR_FIELD]) {
        methods.clearErrors(FORM_API_ERROR_FIELD as Path<T>);
      }
    });

    return () => subscription.unsubscribe?.();
  }, [methods]);

  return {
    onError,
    onErrorAndThrow,
  };
};
